import React, { useRef, useState } from "react";
import AuthService from "Lib/API/Auth";
import { setLocalStorageItem, getAccessTokenSilently } from "utils/functions";
import "./LoginModal.scss";

const LoginModal = ({ onClose, onSuccess, orgLogo }) => {
  const [step, setStep] = useState("email"); // "email" | "otp"
  const [email, setEmail] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);

  const handleSendOtp = async e => {
    e.preventDefault();
    if (!email) return;
    setError("");
    setLoading(true);
    try {
      const res = await AuthService.loginWithOtp({ email });
      setOtpToken(res?.result?.token);
      setStep("otp");
    } catch (err) {
      setError(err?.data?.message || "Failed to send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value, idx) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[idx] = digit;
    setOtp(next);
    if (digit && idx < 5) { inputRefs.current[idx + 1] && inputRefs.current[idx + 1].focus(); }
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      if (inputRefs.current[idx - 1]) inputRefs.current[idx - 1].focus();
      const next = [...otp];
      next[idx - 1] = "";
      setOtp(next);
    }
  };

  const handleOtpPaste = e => {
    e.preventDefault();
    const digits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const next = [...otp];
    digits.split("").forEach((d, i) => { next[i] = d; });
    setOtp(next);
    const target = inputRefs.current[Math.min(digits.length, 5)];
    if (target) target.focus();
  };

  const handleVerify = async e => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) return;
    setError("");
    setLoading(true);
    try {
      const res = await AuthService.otpVerify({ code, token: otpToken });
      setLocalStorageItem("userAuthData", res?.result);
      const token = await getAccessTokenSilently();
      try {
        const userRes = await AuthService.getUserExist(token);
        const u = userRes?.user;
        const org = u?.permissions?.organization;
        localStorage.setItem("accountID", u?.permissions?.linked_account?._id);
        localStorage.setItem("accountName", u?.permissions?.linked_account?.family_name);
        localStorage.setItem("accountInfo", JSON.stringify({
          firstName: u?.permissions?.linked_contact?.first_name,
          lastName: u?.permissions?.linked_contact?.last_name,
          primary_email_address: u?.permissions?.linked_contact?.email_address,
        }));
        localStorage.setItem("hasPayment", u?.hasPayment);
        localStorage.setItem("orgId", org?._id);
        localStorage.setItem("orgName", org?.orgName);
        localStorage.setItem("memberSetting", JSON.stringify(org?.memberSetting || {}));
      } catch (_) {}
      onSuccess();
    } catch (err) {
      setError(err?.data?.message || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lm-overlay" onClick={onClose}>
      <div className="lm" onClick={e => e.stopPropagation()}>
        <button className="lm__close" onClick={onClose} type="button">✕</button>

        {orgLogo && (
          <img src={orgLogo} alt="org logo" className="lm__logo" />
        )}

        {step === "email" ? (
          <form className="lm__form" onSubmit={handleSendOtp}>
            <h2 className="lm__title">Member Login</h2>
            <p className="lm__sub">Enter your email to receive a login code</p>
            <input
              className="lm__input"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoFocus
              required
            />
            {error && <p className="lm__error">{error}</p>}
            <button className="lm__btn" type="submit" disabled={loading}>
              {loading ? "Sending…" : "Send Code"}
            </button>
          </form>
        ) : (
          <form className="lm__form" onSubmit={handleVerify}>
            <h2 className="lm__title">Enter Code</h2>
            <p className="lm__sub">We sent a 6-digit code to <strong>{email}</strong></p>
            <div className="lm__otp-row" onPaste={handleOtpPaste}>
              {otp.map((d, i) => (
                <input
                  key={i}
                  ref={el => inputRefs.current[i] = el}
                  className="lm__otp-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={e => handleOtpChange(e.target.value, i)}
                  onKeyDown={e => handleOtpKeyDown(e, i)}
                />
              ))}
            </div>
            {error && <p className="lm__error">{error}</p>}
            <button className="lm__btn" type="submit" disabled={loading || otp.join("").length < 6}>
              {loading ? "Verifying…" : "Verify"}
            </button>
            <button type="button" className="lm__back" onClick={() => { setStep("email"); setOtp(["","","","","",""]); setError(""); }}>
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
